// ============================================================
// Belge gösterim bileşenleri (§6)
// ============================================================

import { card, emptyNote, sectionTitle } from "./ui.js";
import { esc, fmtDate } from "../lib/fmt.js";
import { belgeDurumu, belgeOzeti, personelBelgeleri, subeBelgeleri } from "../domain/documents.js";

const DURUM = {
  doldu:   { label: "Süresi doldu", renk: "coral", chip: "bg-coral text-white" },
  kritik:  { label: "7 gün",        renk: "coral", chip: "bg-coral text-white" },
  yakin:   { label: "30 gün",       renk: "coral", chip: "bg-coral-soft text-coral" },
  uyari:   { label: "60 gün",       renk: "sun",   chip: "bg-sun-soft text-[#B07E14]" },
  gecerli: { label: "Geçerli",      renk: "teal",  chip: "bg-teal-soft text-teal" },
  suresiz: { label: "Süresiz",      renk: "grape", chip: "bg-grape-soft text-grape" },
};

export const belgeRozeti = belge => {
  const d = belgeDurumu(belge);
  const g = DURUM[d.durum];
  const metin = d.durum === "doldu"
    ? `${Math.abs(d.kalanGun)} gün geçti`
    : d.kalanGun == null ? g.label : `${d.kalanGun} gün`;
  return `<span class="text-[10px] font-bold rounded-full px-2 py-0.5 shrink-0 ${g.chip}">${metin}</span>`;
};

/** Bir kişinin veya şubenin belge listesi. */
export function belgeListesi(belgeler) {
  if (!belgeler.length) return emptyNote("Kayıtlı belge yok.");
  return `
  <ul class="space-y-1.5">
    ${belgeler.map(b => {
      const d = belgeDurumu(b);
      return `
      <li class="flex items-center gap-2 rounded-xl border ${d.durum === "doldu" ? "border-coral" : "border-line"} px-3 py-2">
        <span class="min-w-0 flex-1">
          <span class="flex items-center gap-1.5">
            <span class="text-sm font-semibold truncate">${esc(b.name)}</span>
            ${b.mandatory ? `<span class="text-[9px] font-bold rounded bg-ink text-white px-1 py-0.5 shrink-0">ZORUNLU</span>` : ""}
          </span>
          <span class="block text-[11px] text-inksoft truncate">
            ${b.issuer ? `${esc(b.issuer)} · ` : ""}${b.issuedAt ? `${fmtDate(b.issuedAt)} verildi` : "veriliş tarihi yok"}
            ${b.expiresAt ? ` · ${fmtDate(b.expiresAt)} sonuna kadar` : ""}
          </span>
        </span>
        ${belgeRozeti(b)}
      </li>`;
    }).join("")}
  </ul>`;
}

/** Personel detayında kullanılan kart. */
export function personelBelgeKarti(kisi, branchId) {
  const belgeler = personelBelgeleri(kisi, branchId);
  const sorunlu = belgeler.filter(b => ["doldu", "kritik", "yakin"].includes(belgeDurumu(b).durum));

  return card(`
    <div class="p-5">
      ${sectionTitle(
        "Belgeler & Sertifikalar",
        sorunlu.length
          ? `<span class="text-[11px] font-bold rounded-full bg-coral text-white px-2.5 py-1">${sorunlu.length} işlem gerekiyor</span>`
          : `<span class="text-[11px] font-bold rounded-full bg-teal-soft text-teal px-2.5 py-1">Güncel</span>`,
      )}
      ${belgeListesi(belgeler)}
      <p class="text-[11px] text-inksoft mt-3">
        Geçerlilik süresi belge türünden hesaplanır: İlk Yardım 3 yıl,
        sağlık raporu ve sabıka kaydı 1 yıl. Süresiz belgeler uyarı üretmez.
      </p>
    </div>`, "min-w-0");
}

/** Panel ve Ayarlar'da kullanılan şube geneli uyarı kartı. */
export function belgeUyariKarti(branchId, { baslik = "Süresi Dolan Belgeler", limit = 8 } = {}) {
  const o = belgeOzeti(branchId);
  const satirlar = [
    ...o.sube.map(b => ({ b, kim: "Şube" })),
    ...o.personel.map(x => ({ b: x.belge, kim: x.kisi.name, staffId: x.kisi.id })),
  ].sort((x, y) => belgeDurumu(x.b).kalanGun - belgeDurumu(y.b).kalanGun);

  return card(`
    <div class="p-5">
      ${sectionTitle(
        baslik,
        o.zorunluDolmus
          ? `<span class="text-[11px] font-bold rounded-full bg-coral text-white px-2.5 py-1">${o.zorunluDolmus} zorunlu belge süresi dolmuş</span>`
          : o.toplam
            ? `<span class="text-[11px] font-bold rounded-full bg-sun-soft text-[#B07E14] px-2.5 py-1">${o.toplam} yaklaşan</span>`
            : `<span class="text-[11px] font-bold rounded-full bg-teal-soft text-teal px-2.5 py-1">Hepsi güncel</span>`,
      )}

      ${satirlar.length ? `
        <ul class="space-y-1.5">
          ${satirlar.slice(0, limit).map(({ b, kim, staffId }) => `
            <li ${staffId ? `data-staff="${staffId}" class="cursor-pointer"` : ""}>
              <span class="flex items-center gap-2 rounded-xl border ${belgeDurumu(b).durum === "doldu" ? "border-coral bg-coral-soft/40" : "border-line"} px-3 py-2 hover:bg-paper/60">
                <span class="min-w-0 flex-1">
                  <span class="block text-sm font-semibold truncate">${esc(b.name)}</span>
                  <span class="block text-[11px] text-inksoft truncate">${esc(kim)}${b.expiresAt ? ` · ${fmtDate(b.expiresAt)}` : ""}</span>
                </span>
                ${belgeRozeti(b)}
              </span>
            </li>`).join("")}
        </ul>
        ${satirlar.length > limit ? `<p class="text-[11px] text-inksoft mt-2">+${satirlar.length - limit} belge daha</p>` : ""}`
        : emptyNote("Süresi dolan veya yaklaşan belge yok.")}
    </div>`, "min-w-0");
}

/** Ayarlar'daki şube belgeleri tablosu. */
export function subeBelgeKarti(branchId) {
  return card(`
    <div class="p-5">
      ${sectionTitle("Şube Belgeleri", `<span class="text-[11px] text-inksoft hidden sm:block">Ruhsat, yangın, sigorta</span>`)}
      ${belgeListesi(subeBelgeleri(branchId))}
    </div>`, "min-w-0");
}
